import { Swiper } from 'swiper';
import { Autoplay, EffectFade, Keyboard, Mousewheel, Navigation, Parallax } from 'swiper/modules';

type StudioElement = HTMLElement & {
  __studioOriginalHtml?: string;
  __studioAnimations?: Animation[];
  __studioTimeout?: number;
};

type AnimationConfig = {
  type: string;
  delay: number;
  durationScale: number;
};

export class SwiperStudio {
  private instances: Swiper[] = [];

  constructor() {
    this.init();
  }

  public init(): void {
    const sliders = document.querySelectorAll<HTMLElement>('.swiper-studio');
    if (!sliders.length) return;

    sliders.forEach((slider) => {
      const prevButton = slider.querySelector<HTMLElement>('.swiper-button-prev');
      const nextButton = slider.querySelector<HTMLElement>('.swiper-button-next');

      const swiper = new Swiper(slider, {
        modules: [Autoplay, EffectFade, Keyboard, Mousewheel, Navigation, Parallax],
        loop: slider.dataset.loop !== 'false',
        effect: (slider.dataset.effect || 'fade') as 'fade',
        speed: Number(slider.dataset.speed || 800),
        navigation: {
          enabled: Boolean(prevButton && nextButton),
          prevEl: prevButton || undefined,
          nextEl: nextButton || undefined,
        },
        autoplay: slider.dataset.autoplay === 'false'
          ? false
          : {
              delay: Number(slider.dataset.autoplayDelay || 6000),
              pauseOnMouseEnter: true,
              disableOnInteraction: false,
            },
        keyboard: {
          enabled: true,
        },
        mousewheel: {
          enabled: slider.dataset.mousewheel !== 'false',
          forceToAxis: true,
        },
        parallax: {
          enabled: true,
        },
        watchSlidesProgress: true,
        on: {
          init: (instance) => {
            this.syncAnimations(instance);
          },
          slideChangeTransitionStart: (instance) => {
            this.resetInactiveSlideAnimations(instance);
          },
          slideChangeTransitionEnd: (instance) => {
            this.syncAnimations(instance);
          },
        },
      });

      this.instances.push(swiper);
    });
  }

  private syncAnimations(swiper: Swiper): void {
    this.resetInactiveSlideAnimations(swiper);
    const activeSlide = swiper.slides[swiper.activeIndex] as HTMLElement | undefined;
    if (!activeSlide) return;

    const animatedElements = activeSlide.querySelectorAll<HTMLElement>('[data-swiper-animation]');
    animatedElements.forEach((element) => {
      const config = this.getAnimationConfig(element);
      this.startAnimation(element as StudioElement, config);
    });
  }

  private resetInactiveSlideAnimations(swiper: Swiper): void {
    swiper.slides.forEach((slide, index) => {
      if (index === swiper.activeIndex) return;
      const elements = (slide as HTMLElement).querySelectorAll<HTMLElement>('[data-swiper-animation]');
      elements.forEach((element) => this.resetAnimation(element as StudioElement));
    });
  }

  private getAnimationConfig(element: HTMLElement): AnimationConfig {
    return {
      type: element.dataset.swiperAnimation || 'fade',
      delay: Number(element.dataset.swiperAnimationDelay || 0),
      durationScale: Number(element.dataset.swiperAnimationDurationScale || 1),
    };
  }

  private startAnimation(element: StudioElement, config: AnimationConfig): void {
    this.resetAnimation(element);

    const run = () => {
      switch (config.type) {
        case 'letter-fade':
          this.startLetterFadeAnimation(element, config.durationScale);
          break;
        case 'pop':
          this.startCssAnimation(element, [
            { transform: 'scale(0.85)', opacity: 0 },
            { transform: 'scale(1.06)', opacity: 1, offset: 0.45 },
            { transform: 'scale(1)', opacity: 1 },
          ], 700 * config.durationScale, 'ease-out', 'forwards');
          break;
        case 'zoom':
          this.startCssAnimation(element, [
            { transform: 'scale(1)' },
            { transform: 'scale(1.15)' },
          ], 20000 * config.durationScale, 'linear', 'both', Infinity, 'alternate');
          break;
        case 'fade':
        default:
          this.startCssAnimation(element, [{ opacity: 0 }, { opacity: 1 }], 600 * config.durationScale, 'ease-out', 'forwards');
          break;
      }
    };

    if (config.delay > 0) {
      element.__studioTimeout = window.setTimeout(run, config.delay);
      return;
    }

    run();
  }

  private startCssAnimation(
    element: StudioElement,
    keyframes: Keyframe[],
    duration: number,
    easing: string,
    fill: FillMode,
    iterations: number = 1,
    direction: PlaybackDirection = 'normal'
  ): void {
    const animation = element.animate(keyframes, {
      duration,
      easing,
      fill,
      iterations,
      direction,
    });

    element.__studioAnimations = [animation];
  }

  private startLetterFadeAnimation(element: StudioElement, durationScale: number): void {
    if (!element.__studioOriginalHtml) {
      element.__studioOriginalHtml = element.innerHTML;
    }

    const text = this.getTextFromOriginalHtml(element.__studioOriginalHtml);
    element.innerHTML = '';
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < text.length; i += 1) {
      const span = document.createElement('span');
      span.textContent = text[i];
      span.style.opacity = '0';
      span.style.display = 'inline-block';
      fragment.appendChild(span);
    }

    element.appendChild(fragment);

    const letters = element.querySelectorAll<HTMLElement>('span');
    const totalDuration = 2000 * durationScale;
    const step = letters.length > 0 ? totalDuration / letters.length : totalDuration;
    const fadeDuration = Math.max(200, 420 * durationScale);

    letters.forEach((letter, index) => {
      const timeout = window.setTimeout(() => {
        const animation = letter.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: fadeDuration, fill: 'forwards', easing: 'ease-out' }
        );

        if (!element.__studioAnimations) {
          element.__studioAnimations = [];
        }

        element.__studioAnimations.push(animation);
      }, step * index);

      element.__studioTimeout = timeout;
    });
  }

  private getTextFromOriginalHtml(html: string): string {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    return wrapper.textContent || '';
  }

  private resetAnimation(element: StudioElement): void {
    if (typeof element.__studioTimeout === 'number') {
      clearTimeout(element.__studioTimeout);
      element.__studioTimeout = undefined;
    }

    if (element.__studioAnimations && element.__studioAnimations.length) {
      element.__studioAnimations.forEach((animation) => animation.cancel());
      element.__studioAnimations = [];
    }

    if (element.__studioOriginalHtml) {
      element.innerHTML = element.__studioOriginalHtml;
    }

    element.style.removeProperty('opacity');
    element.style.removeProperty('transform');
    element.style.removeProperty('filter');
  }
}